import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useMeta from '../hooks/useMeta';
import { metaApi, franchiseApi, reportsApi, clinicDataApi } from '../services/api';
import toast from 'react-hot-toast';

import FranchiseReportVisualization from '../components/reports/FranchiseReportVisualization';
import ClinicDataSection from '../components/ClinicDataSection';

const FranchiseReports = () => {
  const { accounts, fetchAccounts } = useMeta();
  const [reportsList, setReportsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // View States: 'list' | 'edit' | 'view'
  const [viewState, setViewState] = useState('list');
  const [currentReportId, setCurrentReportId] = useState(null);

  // Edit State
  const [reportName, setReportName] = useState('Relatório Franquia');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [dateSelectionType, setDateSelectionType] = useState('preset'); // 'preset' | 'custom'
  const [datePreset, setDatePreset] = useState('last_30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Loaded Insights
  const [reportData, setReportData] = useState([]); // Will hold aggregated insights mapped by accountId
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [goals, setGoals] = useState([]);
  const [sharedLink, setSharedLink] = useState('');

  useEffect(() => {
    fetchAccounts();
    loadReports();
  }, [fetchAccounts]);

  const loadReports = async () => {
    setIsLoading(true);
    try {
      const res = await franchiseApi.getReports();
      setReportsList(res.data);
    } catch (err) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditOrCreate = async (id = null) => {
    if (id) {
      try {
        const res = await franchiseApi.getReport(id);
        const rep = res.data;
        setCurrentReportId(rep.id);
        setReportName(rep.name || 'Sem Nome');
        
        if (rep.startDate || rep.endDate) {
          setDateSelectionType('custom');
          setStartDate(rep.startDate ? rep.startDate.split('T')[0] : '');
          setEndDate(rep.endDate ? rep.endDate.split('T')[0] : '');
        } else {
          setDateSelectionType('preset');
          setDatePreset(rep.datePreset || 'last_30d');
        }
        setSelectedAccounts(rep.accountIds || []);
        setGoals(rep.goals || []);
      } catch (err) {
        toast.error('Erro ao abrir relatório');
        return;
      }
    } else {
      setCurrentReportId(null);
      setReportName('Novo Relatório Franquia');
      setDateSelectionType('preset');
      setDatePreset('last_30d');
      setSelectedAccounts([]);
      setGoals([]);
    }
    setViewState('edit');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja apagar este relatório?')) {
      try {
        await franchiseApi.deleteReport(id);
        toast.success('Deletado com sucesso');
        loadReports();
      } catch (err) {
        toast.error('Erro ao deletar');
      }
    }
  };

  const toggleAccount = (accountId) => {
    if (selectedAccounts.includes(accountId)) {
      setSelectedAccounts(selectedAccounts.filter(a => a !== accountId));
    } else {
      setSelectedAccounts([...selectedAccounts, accountId]);
    }
  };

  const generateInsights = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Selecione ao menos uma conta');
      return;
    }

    setIsLoading(true);
    const params = { level: 'account' }; // Force account level to aggregate and get unique reach
    if (dateSelectionType === 'preset') {
      params.datePreset = datePreset;
    } else {
      if (!startDate || !endDate) {
        toast.error('Selecione data inicio e fim');
        setIsLoading(false);
        return;
      }
      params.startDate = startDate;
      params.endDate = endDate;
    }

    try {
      // Fetch insights for all selected accounts in parallel
      const promises = selectedAccounts.map(accountId => 
        metaApi.getInsights(accountId, params).catch(() => ({ data: [] }))
      );
      
      const results = await Promise.all(promises);

      // Aggregate data per account logic
      // results[i].data is array of account insights for account i (usually 1 item)
      const aggregatedData = selectedAccounts.map((accountId, index) => {
        const accInfo = accounts.find(a => (a.account_id || a.metaAccountId || a.id.replace('act_','')) === accountId);
        const insights = results[index].data || [];
        
        // Sum metrics 
        const totals = insights.reduce((acc, curr) => {
             let leads = 0;
             let messages = 0;
             let conversions = 0;

             if (curr.actions && Array.isArray(curr.actions)) {
               // Prioridade de Campo: 1 ONSITE -> 2 GENERIC LEAD
               const onsiteLead = curr.actions.find(a => a.action_type === 'onsite_conversion.lead_grouped');
               const genericLead = curr.actions.find(a => a.action_type === 'lead');
               
               if (onsiteLead) {
                 leads += parseInt(onsiteLead.value) || 0;
               } else if (genericLead) {
                 leads += parseInt(genericLead.value) || 0;
               }

               curr.actions.forEach(action => {
                 const type = action.action_type;
                 const val = parseInt(action.value) || 0;
                 
                 // Messages
                 if (type === 'onsite_conversion.messaging_conversation_started_7d' || type === 'contact') messages += val;
               });
             }

             conversions = leads + messages;

             return {
                 spend: acc.spend + (parseFloat(curr.spend) || 0),
                 impressions: acc.impressions + parseInt(curr.impressions || 0, 10),
                 reach: acc.reach + parseInt(curr.reach || 0, 10),
                 conversions: acc.conversions + conversions,
                 leads: acc.leads + leads,
                 messages: acc.messages + messages,
             };
        }, { spend: 0, impressions: 0, reach: 0, conversions: 0, leads: 0, messages: 0 });

        // Try to properly aggregate leads and messages if they are not in standard conversions
        // This depends heavily on real Meta API response structure which varies. For this implementation we will infer.

        // Initialize goals structure if not exists
        const existingGoal = goals.find(g => g.accountId === accountId);

        return {
          accountId,
          accountName: accInfo ? accInfo.name : 'Desconhecida',
          totals,
          revenueGoal: existingGoal ? parseFloat(existingGoal.revenueGoal) : 0,
          actualRevenue: existingGoal ? parseFloat(existingGoal.actualRevenue) : 0
        };
      });

      setReportData(aggregatedData);
      setViewState('view');
    } catch (err) {
      toast.error('Falha ao processar dados das contas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveReport = async () => {
    try {
      const payload = {
        name: reportName,
        datePreset: dateSelectionType === 'preset' ? datePreset : null,
        startDate: (dateSelectionType === 'custom' || dateSelectionType === 'current_month') ? startDate : null,
        endDate: (dateSelectionType === 'custom' || dateSelectionType === 'current_month') ? endDate : null,
        accountIds: selectedAccounts,
        goals: reportData.map(d => ({
          accountId: d.accountId,
          revenueGoal: d.revenueGoal,
          actualRevenue: d.actualRevenue
        }))
      };

      if (currentReportId) {
        await franchiseApi.updateReport(currentReportId, payload);
        toast.success('Atualizado com sucesso');
      } else {
        const res = await franchiseApi.createReport(payload);
        setCurrentReportId(res.data.id);
        toast.success('Criado com sucesso');
      }
      
      // Update goals cache
      setGoals(payload.goals);
      
    } catch (err) {
      toast.error('Erro ao salvar relatório');
    }
  };

  const handleShareReport = async () => {
    try {
      // Snapshot clinic data KPIs too, mirroring the live section rendered
      // below the report (ClinicDataSection) — shared link is a static snapshot.
      const clinicDbIds = selectedAccounts
        .map(id => accounts.find(a => (a.account_id || a.metaAccountId) === id || a.id?.replace('act_', '') === id)?.id)
        .filter(Boolean);
      const clinicStart = dateSelectionType !== 'preset' ? startDate : undefined;
      const clinicEnd = dateSelectionType !== 'preset' ? endDate : undefined;
      let clinicSnapshot = null;
      if (clinicDbIds.length > 0) {
        clinicSnapshot = await clinicDataApi.getAccountData(clinicDbIds, clinicStart, clinicEnd)
          .then(res => res.data || [])
          .catch(() => null);
      }

      const payload = {
        reportType: 'franchise',
        entityId: currentReportId || 'temporario-franchise',
        entityName: reportName,
        options: {
           franchiseData: reportData,
           periodLabel: dateSelectionType === 'preset' ? datePreset : `${startDate} a ${endDate}`,
           snapshotClinicData: clinicSnapshot,
        }
      };

      const res = await reportsApi.shareReport(payload);
      
      setSharedLink(res.data.url);
      
      // Módulo de copiado pra a área de transferência do usuário
      navigator.clipboard.writeText(res.data.url)
        .then(() => toast.success('Relatório gerado! Link copiado para a área de transferência.'))
        .catch(() => toast.success(`Compartilhado: ${res.data.url}`));
        
    } catch (err) {
      toast.error('Erro ao criar link de compartilhamento.');
    }
  };

  const updateGoal = (accountId, field, value) => {
    // Update reportData state which feeds the table
    const updated = reportData.map(d => {
      if (d.accountId === accountId) {
        return { ...d, [field]: value };
      }
      return d;
    });
    setReportData(updated);
  };

  // Views List
  if (viewState === 'list') {
    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-2">Relatórios Franquia</h1>
            <p className="text-secondary font-body">Gerencie e analise múltiplas contas simultaneamente.</p>
          </div>
          <button onClick={() => handleEditOrCreate()} className="primary-pill-gradient text-white px-6 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2">
            <span className="material-symbols-outlined">add</span> Novo Relatório
          </button>
        </header>

        {isLoading ? (
          <div className="flex justify-center py-20"><span className="material-symbols-outlined animate-spin text-4xl text-primary">cycle</span></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {reportsList.length === 0 ? (
              <div className="col-span-full py-20 text-center text-secondary border border-dashed rounded-xl border-slate-300">
                <span className="material-symbols-outlined text-4xl mb-2 opacity-50">data_alert</span>
                <p>Nenhum relatório salvo encontrado.</p>
              </div>
            ) : (
              reportsList.map(rep => (
                <div key={rep.id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{rep.name}</h3>
                    <p className="text-xs text-slate-500 mb-4 font-label uppercase tracking-wide">
                      {rep.datePreset ? rep.datePreset : `${new Date(rep.startDate).toLocaleDateString()} - ${new Date(rep.endDate).toLocaleDateString()}`}
                      &nbsp;&bull; {rep.accountIds?.length || 0} contas
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-50">
                    <button onClick={() => handleEditOrCreate(rep.id)} className="flex-1 bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors py-2 rounded-lg font-bold text-sm">
                      Abrir
                    </button>
                    <button onClick={() => handleDelete(rep.id)} className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors">
                      <span className="material-symbols-outlined text-sm">delete</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  // Edit/Config View (Selection)
  if (viewState === 'edit') {
    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-[1000px] mx-auto w-full">
        <header className="mb-8 flex items-center gap-4">
          <button onClick={() => setViewState('list')} className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm hover:bg-slate-50 text-slate-600 transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div className="flex-1">
            <input 
              className="text-3xl lg:text-4xl font-extrabold font-headline tracking-tight text-on-surface bg-transparent border-none outline-none w-full placeholder:text-slate-300" 
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Nome do Relatório"
            />
          </div>
        </header>

        <div className="bg-surface-container-lowest rounded-xl p-6 lg:p-10 shadow-[0_8px_32px_rgba(205,25,249,0.04)] space-y-8">
          
          <div className="space-y-3">
             <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Período de Análise</label>
             <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
               {['last_7d', 'last_14d', 'last_30d', 'lifetime'].map(preset => (
                  <button key={preset}
                    className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'preset' && datePreset === preset ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    onClick={() => { setDateSelectionType('preset'); setDatePreset(preset); }}
                  >
                    {preset === 'last_7d' ? '7 Dias' : preset === 'last_14d' ? '14 Dias' : preset === 'last_30d' ? '30 Dias' : 'Vitalício'}
                  </button>
               ))}
               <button
                  className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'current_month' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                  onClick={() => {
                    const d = new Date();
                    setDateSelectionType('current_month');
                    setStartDate(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]);
                    setEndDate(d.toISOString().split('T')[0]);
                  }}
                >Mês Atual</button>
               <button
                  className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${dateSelectionType === 'custom' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                  onClick={() => setDateSelectionType('custom')}
                >Personalizado</button>
             </div>
             {(dateSelectionType === 'custom' || dateSelectionType === 'current_month') && (
                  <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-surface-container-low rounded-xl animate-fade-in">
                    <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                  </div>
             )}
          </div>

          <div className="space-y-3">
             <div className="flex justify-between items-end">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Contas Selecionadas ({selectedAccounts.length})</label>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
               {accounts.map(acc => {
                  const id = acc.account_id || acc.metaAccountId || acc.id.replace('act_', '');
                  const isSelected = selectedAccounts.includes(id);
                  return (
                     <div key={id} 
                       onClick={() => toggleAccount(id)}
                       className={`p-4 rounded-xl cursor-pointer border-2 transition-all flex items-center justify-between ${isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}
                     >
                        <div className="truncate pr-2">
                          <p className={`font-bold text-sm truncate ${isSelected ? 'text-primary' : 'text-slate-700'}`}>{acc.name}</p>
                          <p className="text-[10px] text-slate-400 font-mono mt-1">{id}</p>
                        </div>
                        {isSelected && <span className="material-symbols-outlined text-primary">check_circle</span>}
                     </div>
                  )
               })}
             </div>
          </div>

          <div className="pt-6 border-t border-slate-100 flex justify-end">
             <button 
               onClick={generateInsights}
               disabled={isLoading || selectedAccounts.length === 0}
               className="primary-pill-gradient text-white px-8 py-4 rounded-full font-headline text-lg font-extrabold shadow-lg hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
             >
               {isLoading ? <span className="material-symbols-outlined animate-spin">refresh</span> : <span className="material-symbols-outlined">analytics</span>}
               {isLoading ? 'Processando dados...' : 'Ir para Relatório'}
             </button>
          </div>
        </div>
      </div>
    )
  }

  // View 'view' is the generated report display
  if (viewState === 'view') {
    const clinicDbIds = selectedAccounts.map(id =>
      accounts.find(a => (a.account_id || a.metaAccountId) === id || a.id?.replace('act_', '') === id)?.id
    ).filter(Boolean);
    const clinicStart = dateSelectionType !== 'preset' ? startDate : undefined;
    const clinicEnd = dateSelectionType !== 'preset' ? endDate : undefined;
    return (
      <FranchiseReportVisualization
         reportName={reportName}
         data={reportData}
         updateGoal={updateGoal}
         onSave={handleSaveReport}
         onShare={handleShareReport}
         sharedLink={sharedLink}
         onBack={() => setViewState('edit')}
         isSaving={isLoading}
         periodLabel={dateSelectionType === 'preset' ? datePreset : `${startDate} a ${endDate}`}
         clinicSection={clinicDbIds.length > 0 ? <ClinicDataSection adAccountIds={clinicDbIds} startDate={clinicStart} endDate={clinicEnd} /> : null}
      />
    );
  }

  return null;
};

export default FranchiseReports;
